import React, { useState, useEffect } from 'react';
import { getAllMunicipalities, Municipality, getFeesForYear, filterFees, FEE_CATEGORIES, DEFAULT_ENABLED_CATEGORIES } from '@/lib/data';
import { getLatestYear, getYearsAsNumbers } from '@/config/years';
import { StatsWidget } from './StatsWidget';
import { KommuneWidget } from './KommuneWidget';
import { FylkeWidget } from './FylkeWidget';
import { TopplisteWidget } from './TopplisteWidget';
import { BadgeWidget } from './BadgeWidget';
import { CompactWidget } from './CompactWidget';
import { BreakdownWidget } from './BreakdownWidget';
import { MiniBarsWidget } from './MiniBarsWidget';

export type WidgetType =
  | 'stats'
  | 'kommuner'
  | 'fylker'
  | 'topp'
  | 'badge'
  | 'compact'
  | 'breakdown'
  | 'mini';

export interface WidgetConfig {
  type: WidgetType;
  year?: number;
  show?: ('highest' | 'lowest' | 'average' | 'increase')[];
  kommuner?: string[];
  fylker?: string[];
  limit?: number;
  sort?: 'asc' | 'desc';
  theme?: 'light' | 'dark';
  bgColor?: string;
  categories?: string[];
}

export const parseConfig = (params: URLSearchParams): WidgetConfig => {
  const validTypes: WidgetType[] = [
    'stats',
    'kommuner',
    'fylker',
    'topp',
    'badge',
    'compact',
    'breakdown',
    'mini',
  ];
  const type = (params.get('type') || 'stats') as WidgetType;
  const resolvedType = validTypes.includes(type) ? type : 'stats';

  const yearParam = params.get('ar') || params.get('year');
  const years = getYearsAsNumbers();
  const year = yearParam ? parseInt(yearParam, 10) : getLatestYear();
  const resolvedYear = years.includes(year) ? year : getLatestYear();

  const showParam = params.get('show') || 'highest,lowest,average';
  const show = showParam
    .split(',')
    .filter(
      (s): s is 'highest' | 'lowest' | 'average' | 'increase' =>
        ['highest', 'lowest', 'average', 'increase'].includes(s.trim())
    ) as ('highest' | 'lowest' | 'average' | 'increase')[];
  if (show.length === 0) show.push('highest', 'lowest', 'average');

  const kommunerParam = params.get('kommuner') || '';
  const kommuner = kommunerParam
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

  const fylkerParam = params.get('fylker') || '';
  const fylker = fylkerParam
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

  const limit = Math.min(20, Math.max(1, parseInt(params.get('limit') || '5', 10)));
  const sort = (params.get('sort') || 'desc') === 'asc' ? 'asc' : 'desc';
  const theme = params.get('theme') === 'dark' ? 'dark' : 'light';

  const bgParam = params.get('bg') || params.get('bgColor') || '';
  const bgColor = /^[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(bgParam)
    ? '#' + bgParam
    : bgParam.startsWith('#') && /^#[0-9a-fA-F]{3,8}$/.test(bgParam)
    ? bgParam
    : undefined;

  const validCategoryIds = new Set(FEE_CATEGORIES.map(c => c.id));
  const categoriesParam = params.get('kategorier') || params.get('categories') || '';
  const categories = categoriesParam
    .split(',')
    .map(s => s.trim())
    .filter(id => validCategoryIds.has(id));

  return {
    type: resolvedType,
    year: resolvedYear,
    show: show.length > 0 ? show : ['highest', 'lowest', 'average'],
    kommuner: kommuner.length > 0 ? kommuner : undefined,
    fylker: fylker.length > 0 ? fylker : undefined,
    limit,
    sort,
    theme,
    bgColor,
    categories: categories.length > 0 ? categories : DEFAULT_ENABLED_CATEGORIES,
  };
};

interface WidgetEmbedProps {
  config: WidgetConfig;
  baseUrl?: string;
}

const getTopMunicipalities = (
  municipalities: Municipality[],
  year: number,
  count: number,
  enabledCategories: string[]
): Municipality[] =>
  municipalities
    .map(m => ({
      m,
      total: filterFees(getFeesForYear(m, year), enabledCategories).reduce((s, f) => s + f.amount, 0),
    }))
    .filter(x => x.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, count)
    .map(x => x.m);

export const WidgetEmbed: React.FC<WidgetEmbedProps> = ({ config, baseUrl }) => {
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getAllMunicipalities().then(setMunicipalities).finally(() => setIsLoading(false));
  }, []);

  const filteredByKommune = config.kommuner?.length
    ? municipalities.filter(m => config.kommuner!.includes(m.id))
    : municipalities;

  const filteredByFylke = config.fylker?.length
    ? filteredByKommune.filter(m => config.fylker!.includes(m.county))
    : filteredByKommune;

  const displayMunicipalities = filteredByFylke;
  const enabledCategories = config.categories?.length ? config.categories : DEFAULT_ENABLED_CATEGORIES;
  const dark = config.theme === 'dark';

  const wrapperCls = dark
    ? 'bg-slate-900 border-slate-700/50'
    : 'bg-white border-gray-200';
  const headerCls = dark
    ? 'bg-slate-800/80 border-slate-700/50 text-slate-200'
    : 'bg-nordicBlue/10 border-gray-100 text-deepCharcoal';
  const linkCls = dark ? 'text-slate-500 hover:text-slate-400' : 'text-warmGray hover:text-nordicBlue';

  if (isLoading) {
    return (
      <div className={`rounded-xl border ${wrapperCls} p-4`}>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
          <div className="h-12 bg-slate-200 dark:bg-slate-700 rounded" />
          <div className="h-12 bg-slate-200 dark:bg-slate-700 rounded" />
        </div>
      </div>
    );
  }

  const subtitle =
    config.fylker?.length
      ? config.fylker.join(', ')
      : config.kommuner?.length
      ? `${config.kommuner.length} kommuner`
      : 'Norge';

  const topMunies =
    displayMunicipalities.length > 0
      ? displayMunicipalities
      : getTopMunicipalities(municipalities, config.year!, 10, enabledCategories);

  const singleKommune =
    config.kommuner?.length === 1
      ? displayMunicipalities.find(m => m.id === config.kommuner![0]) ??
        municipalities.find(m => m.id === config.kommuner![0])
      : topMunies[0];

  return (
    <div className={`rounded-xl border ${wrapperCls} overflow-hidden`}>
      <div className={`px-4 py-3.5 border-b ${headerCls}`}>
        <p className="text-xs font-semibold uppercase tracking-wider">
          Kommunale avgifter {config.year}
        </p>
        <p className={`text-[11px] mt-0.5 ${dark ? 'text-slate-400' : 'text-warmGray'}`}>{subtitle}</p>
      </div>
      <div className={`p-4 ${dark ? 'bg-slate-900' : 'bg-white'}`}>
        {config.type === 'stats' && (
          <StatsWidget
            municipalities={displayMunicipalities.length > 0 ? displayMunicipalities : municipalities}
            year={config.year!}
            show={config.show || ['highest', 'lowest', 'average']}
            enabledCategories={enabledCategories}
            dark={dark}
          />
        )}
        {config.type === 'kommuner' && (
          <KommuneWidget
            municipalities={
              displayMunicipalities.length > 0
                ? displayMunicipalities
                : topMunies
            }
            year={config.year!}
            enabledCategories={enabledCategories}
            dark={dark}
          />
        )}
        {config.type === 'fylker' && (
          <FylkeWidget
            municipalities={displayMunicipalities}
            counties={
              config.fylker?.length
                ? config.fylker
                : [...new Set(municipalities.map(m => m.county))].filter(Boolean).sort()
            }
            year={config.year!}
            enabledCategories={enabledCategories}
            dark={dark}
          />
        )}
        {config.type === 'topp' && (
          <TopplisteWidget
            municipalities={displayMunicipalities.length > 0 ? displayMunicipalities : municipalities}
            year={config.year!}
            limit={config.limit || 5}
            sort={config.sort || 'desc'}
            enabledCategories={enabledCategories}
            dark={dark}
          />
        )}
        {config.type === 'badge' && singleKommune && (
          <BadgeWidget municipality={singleKommune} year={config.year!} enabledCategories={enabledCategories} dark={dark} />
        )}
        {config.type === 'compact' && (
          <CompactWidget
            municipalities={topMunies.slice(0, 4)}
            year={config.year!}
            limit={4}
            enabledCategories={enabledCategories}
            dark={dark}
          />
        )}
        {config.type === 'breakdown' && singleKommune && (
          <BreakdownWidget municipality={singleKommune} year={config.year!} enabledCategories={enabledCategories} dark={dark} />
        )}
        {config.type === 'mini' && (
          <MiniBarsWidget
            municipalities={topMunies.slice(0, config.limit || 4)}
            year={config.year!}
            limit={config.limit || 4}
            enabledCategories={enabledCategories}
            dark={dark}
          />
        )}
      </div>
      {baseUrl && (
        <p className={`px-4 pb-3 text-center`}>
          <a
            href={baseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-[10px] ${linkCls} transition-colors`}
          >
            kommunaleavgifter.no
          </a>
        </p>
      )}
    </div>
  );
};
